import { ChannelCardV2, PackageSummary } from "lib/helper/channel-interactive-tool-v2";
import { KeyValuePair } from "@directv-digital/dds.services.channel-lineup-provider";
import {
  handleLocalRsnChannelFilter,
  buildLocalChannelLookup,
  filterDuplicateNationalChannels
} from "lib/service/channels/get-local-national-dma-eligible-channels";

/**
 * Converts local/RSN API channels to ChannelCardV2 format
 * Note: Server already provides RSN and LOCAL categories in bannerCategories
 * This utility only converts the channel data from zipcode module API
 * @param localRsnChnlsData - Data from zipcode module API
 * @returns Object with LOCAL and RSN channel arrays
 */
export const convertLocalRsnToChannelCards = (localRsnChnlsData: {
  localChannels: any[];
  rsnChannels: any[];
}): { localChannels: ChannelCardV2[]; rsnChannels: ChannelCardV2[] } => {
  const convertChannel = (channel: any, type: "local" | "rsn"): ChannelCardV2 => {
    return {
      id: channel.callSign || channel.network,
      label: channel.network || channel.callSign,
      displayText: channel.network || channel.callSign,
      logo: channel.icon || channel.logo,
      categoryId: type.toUpperCase(),
      // Structural markers — the stable signal consumers gate on (categoryId is
      // overwritten to the pack key once a local channel is injected into a genre
      // pack). Set alongside the existing categoryId assignment, not in place of it.
      isLocal: type === "local",
      isRsn: type === "rsn",
      // LOCAL channels are always clickable — signature exclusion is handled in the modal
      // RSN channels check packageNames
      hasIncludedPackages: type === "local" ? true : Boolean(channel.packageNames?.length > 0),
      signaturePackages: [], // Will be built on-the-fly in modal
      genrePackages: [], // Will be built on-the-fly in modal
      // Store raw channel data for modal use
      rawData: {
        ...channel,
        category: type.toUpperCase(),
      },
    };
  };

  return {
    localChannels: (localRsnChnlsData.localChannels || []).map(ch => convertChannel(ch, "local")),
    rsnChannels: (localRsnChnlsData.rsnChannels || []).map(ch => convertChannel(ch, "rsn"))
  };
};

/**
 * Merges local/RSN channels into channelsByCategory
 * Server provides empty RSN and LOCAL category keys in channelsByCategory
 * This function populates them with actual channel data from zipcode module
 * LOCAL/RSN channels are fetched dynamically per zipcode, so they're always full objects
 *
 * Also filters out duplicate national channels based on local channel availability
 *
 * @param channelsByCategory - Existing channels by category (can be normalized or denormalized)
 * @param localChannels - Converted local channels
 * @param rsnChannels - Converted RSN channels
 * @param channelMap - Channel map for denormalization (optional)
 * @param dmaData - Current DMA information
 * @param genreLocalMapping - Genre-to-DMA eligibility mapping
 * @param genreNationalMapping - National channel aliases mapping
 * @param packageSummaries - Package summaries for genre pack identification
 * @returns Updated channelsByCategory with LOCAL and RSN populated and nationals filtered
 */
export const mergeLocalRsnChannels = (
  channelsByCategory: Record<string, ChannelCardV2[]> | Record<string, string[]>,
  localChannels: ChannelCardV2[],
  rsnChannels: ChannelCardV2[],
  channelMap?: Record<string, ChannelCardV2>,
  dmaData?: { dma?: string; nielsenDma?: string } | null,
  genreLocalMapping?: any[],
  genreNationalMapping?: any[],
  packageSummaries?: Record<string, PackageSummary>
): Record<string, ChannelCardV2[] | string[]> => {
  const result = { ...channelsByCategory };

  // Populate LOCAL and RSN categories (always full channel objects)
  if (localChannels.length > 0) {
    result.LOCAL = localChannels;
  }

  if (rsnChannels.length > 0) {
    result.RSN = rsnChannels;
  }

  // Filter duplicate nationals from other categories
  try {
    // Build lookup for local/RSN channels
    const localChannelLookup = buildLocalChannelLookup(
      localChannels.map(ch => ch.rawData || ch),
      rsnChannels.map(ch => ch.rawData || ch),
      genreNationalMapping
    );

    // Skip filtering if no local/RSN channels or no mapping data
    if (localChannelLookup.baseChannelMap.size === 0 || !genreNationalMapping) {
      return result;
    }

    // Filter nationals from each category (except LOCAL and RSN)
    let totalFiltered = 0;
    Object.keys(result).forEach(categoryKey => {
      if (categoryKey === "LOCAL" || categoryKey === "RSN") {
        return; // Skip local/RSN categories
      }

      const channels = result[categoryKey];
      if (!Array.isArray(channels) || channels.length === 0) {
        return;
      }

      const beforeCount = channels.length;
      // Filter duplicates
      const filteredChannels = filterDuplicateNationalChannels(
        channels,
        localChannelLookup,
        channelMap,
        dmaData,
        genreLocalMapping,
        genreNationalMapping,
        packageSummaries
      );

      result[categoryKey] = filteredChannels;
      totalFiltered += (beforeCount - filteredChannels.length);
    });

    if (totalFiltered > 0) {
      console.log('[mergeLocalRsnChannels] Removed', totalFiltered, 'duplicate nationals');
    }
  } catch (error) {
    console.error("mergeLocalRsnChannels deduplication error:", error);
    // If error occurs, return result with LOCAL/RSN populated but no filtering
  }

  return result;
};

/**
 * Checks if a channel is a local or RSN channel
 *
 * Detects via the structural flags set at the source. `categoryId` is kept as a
 * Stage-1 safety net: it stays "LOCAL"/"RSN" for cards from
 * convertLocalRsnToChannelCards but is overwritten to the pack key once a local
 * channel is injected into a genre pack — which is exactly why the flags are the
 * primary signal.
 * @param channel - Channel to check
 * @returns true if channel is local or RSN
 */
export const isLocalOrRsnChannel = (channel: ChannelCardV2): boolean => {
  return Boolean(channel.isLocal || channel.isRsn) || channel.categoryId === "LOCAL" || channel.categoryId === "RSN";
};

/**
 * Comprehensive check to determine if a channel should be routed to RSN modal
 * Primary signal is the `isRsn` flag; rawData (TYPE/category/rsntier) is retained
 * as a safety net for zipcode-module channels that carry only raw data.
 * @param channel - Channel to check
 * @returns true if channel should use RSN modal
 */
export const isRsnChannel = (channel: ChannelCardV2): boolean => {
  // Structural flag — set at the source for RSN channels.
  if (channel.isRsn) {
    return true;
  }

  // Check rawData for zipcode-module RSN channels
  if (channel.rawData) {
    const rawType = channel.rawData.TYPE || channel.rawData.category;
    if (rawType === "RSN") {
      return true;
    }

    // Check for rsntier property (can be 0, so check for undefined/null)
    if (channel.rawData.rsntier !== undefined && channel.rawData.rsntier !== null) {
      return true;
    }
  }

  return false;
};

// Generated by Copilot- Start - 2026-03-18 19:46:46
/**
 * Merges local/RSN channels into genre category buckets when zipcode changes.
 * For each genre category (e.g. MYSPORTS, MYENTERTAINMENT):
 *   - Looks up the packageSummary to get the queryParam (e.g. "mysports")
 *   - Calls handleLocalRsnChannelFilter to find eligible local/RSN channels for that genre
 *   - Converts eligible channels to ChannelCardV2 with isLocalChannel:true for badge rendering
 *   - Appends them to the existing channelsByCategory[key] (string refs stay; objects are appended)
 *   - Filters out duplicate national channels based on local/RSN availability
 *
 * RSN channels are only injected into the MYHOMETEAM category (packageKey === "myhometeam").
 * Local channels are injected into all other genre categories based on DMA eligibility.
 *
 * @param localRsnChnlsData - Raw local/RSN data from zipcode API
 * @param bannerCategories - Genre category list from server (e.g. [{key:"MYSPORTS",value:"My Sports"},...])
 * @param packageSummaries - Map of package id to PackageSummary (keyed as ATT_TV_MYSPORTS, etc.)
 * @param genreLocalChannelMapping - DMA-to-genre channel eligibility mapping
 * @param dmaData - DMA codes from zipcode lookup
 * @param genreNationals - Genre national channel mapping data (contains genreNationalMapping array)
 * @param existingChannelsByCategory - Existing channelsByCategory (string refs from server)
 * @param channelMap - Channel map for denormalization (optional)
 * @returns Updated channelsByCategory with local/RSN channels injected per genre category and nationals filtered
 */
export const mergeLocalRsnIntoGenreCategories = (
  localRsnChnlsData: { localChannels: any[]; rsnChannels: any[] },
  bannerCategories: KeyValuePair[],
  packageSummaries: Record<string, PackageSummary>,
  genreLocalChannelMapping: any[] | undefined,
  dmaData: { dma?: string; nielsenDma?: string } | null,
  genreNationals: any,
  existingChannelsByCategory: Record<string, string[] | ChannelCardV2[]>,
  channelMap?: Record<string, ChannelCardV2>
): Record<string, string[] | ChannelCardV2[]> => {
  const result: Record<string, string[] | ChannelCardV2[]> = { ...existingChannelsByCategory };

  // Nothing to inject if no local/RSN data
  const allLocalRsn = [...(localRsnChnlsData.localChannels || []), ...(localRsnChnlsData.rsnChannels || [])];

  if (!allLocalRsn.length) {
    return result;
  }

  const dma = dmaData?.dma || "";
  const nielsenDma = dmaData?.nielsenDma;
  const genreNationalMapping = genreNationals?.genreNationalMapping;

  // Build local channel lookup for deduplication
  let localChannelLookup: ReturnType<typeof buildLocalChannelLookup> | null = null;
  try {
    localChannelLookup = buildLocalChannelLookup(
      localRsnChnlsData.localChannels || [],
      localRsnChnlsData.rsnChannels || [],
      genreNationalMapping
    );
  } catch (error) {
    console.error("mergeLocalRsnIntoGenreCategories: buildLocalChannelLookup error", error);
  }

  let totalEligibleInjected = 0;
  let totalNationalsFiltered = 0;

  bannerCategories.forEach(cat => {
    const catKey = cat.key; // e.g. "ATT_TV_MYSPORTS" (already full ID from adapter)
    const packageId = catKey; // Use key directly - no need to prepend ATT_TV_
    const queryParam = packageSummaries[packageId]?.queryParam; // e.g. "mysports"

    if (!queryParam) {
      return;
    }

    // Inject eligible local/RSN channels
    const eligibleRawChannels = handleLocalRsnChannelFilter(
      allLocalRsn,
      genreLocalChannelMapping || [],
      queryParam,
      dma,
      nielsenDma,
      genreNationalMapping
    );

    const eligibleCards: ChannelCardV2[] = eligibleRawChannels.map((ch: any) => ({
      id: ch.callSign || ch.network || ch.name,
      label: ch.network || ch.name || ch.callSign,
      displayText: ch.network || ch.name || ch.callSign,
      logo: ch.icon || ch.logo,
      categoryId: catKey,
      hasIncludedPackages: true,
      signaturePackages: [],
      genrePackages: [],
      isLocalChannel: true,
      rawData: {
        ...ch,
        category: queryParam === "myhometeam" ? "RSN" : "LOCAL"
      }
    }));

    // Filter duplicate nationals from existing channels in this category
    let existingChannels = result[catKey] || [];
    const beforeFilterCount = Array.isArray(existingChannels) ? existingChannels.length : 0;

    if (localChannelLookup && localChannelLookup.baseChannelMap.size > 0 && genreNationalMapping) {
      try {
        existingChannels = filterDuplicateNationalChannels(
          existingChannels,
          localChannelLookup,
          channelMap,
          dmaData,
          genreLocalChannelMapping,
          genreNationalMapping,
          packageSummaries
        );
      } catch (error) {
        console.error(`mergeLocalRsnIntoGenreCategories: filterDuplicateNationalChannels error for category ${catKey}`, error);
        // On error, keep existing channels as-is
      }
    }

    const afterFilterCount = Array.isArray(existingChannels) ? existingChannels.length : 0;
    const filtered = beforeFilterCount - afterFilterCount;
    totalNationalsFiltered += filtered;

    // Append eligible local/RSN channels
    if (eligibleCards.length > 0) {
      result[catKey] = [...existingChannels, ...eligibleCards] as ChannelCardV2[] | string[];
      totalEligibleInjected += eligibleCards.length;
    } else {
      result[catKey] = existingChannels;
    }
  });

  if (totalEligibleInjected > 0 || totalNationalsFiltered > 0) {
    console.log('[mergeLocalRsnIntoGenreCategories]', {
      injected: totalEligibleInjected,
      removed: totalNationalsFiltered
    });
  }

  return result;
};
// Generated by Copilot- End - 2026-03-18 19:46:46
